import { useState, useCallback } from 'react';

/**
 * Custom React hook for form validation.
 *
 * @param {object} initialValues - Initial form field values.
 * @param {object} validationRules - Rules configuration mapped by field name.
 *   Each field's value can be:
 *   - An array of rules: [{ check: Function, message: String, validateOnChange?: Boolean }]
 *   - An object: { rules: Array, generalError: String }
 */
export function useFormValidation(initialValues, validationRules = {}) {
  const [formData, setFormData] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  /**
   * Handles changes to individual form fields.
   * Validates the field immediately in specific conditions (e.g. required field, or if there is already an error).
   */
  const handleChange = useCallback((field, value) => {
    setFormData((prevValues) => {
      const prevValue = prevValues[field];
      const nextValues = { ...prevValues, [field]: value };

      const fieldConfig = validationRules[field];
      if (fieldConfig) {
        const rules = Array.isArray(fieldConfig) ? fieldConfig : fieldConfig.rules || [];
        const generalError = fieldConfig.generalError;

        let firstFailedRule = null;
        for (const rule of rules) {
          if (!rule.check(value, nextValues)) {
            firstFailedRule = rule;
            break;
          }
        }

        // We check against the current errors state.
        // If the field already has an error, we validate on change to update/clear it immediately.
        // If the field doesn't have an error, we only show it on change if the failed rule specifies
        // validateOnChange AND the field previously had a value (e.g. user cleared it).
        // This avoids false "required" errors when browser autofill fires empty change events on open.
        setErrors((prevErrors) => {
          const hasExistingError = !!prevErrors[field];
          const prevHadValue = typeof prevValue === 'string'
            ? prevValue.trim() !== ''
            : Array.isArray(prevValue)
              ? prevValue.length > 0
              : prevValue !== undefined && prevValue !== null && prevValue !== '';

          if (firstFailedRule) {
            if (hasExistingError || (firstFailedRule.validateOnChange && prevHadValue)) {
              return {
                ...prevErrors,
                [field]: generalError || firstFailedRule.message,
              };
            }
          } else {
            if (hasExistingError) {
              const nextErrors = { ...prevErrors };
              delete nextErrors[field];
              return nextErrors;
            }
          }
          return prevErrors;
        });
      }

      return nextValues;
    });
  }, [validationRules]);

  /**
   * Handles blur events to mark a field as touched and run full validation for it.
   */
  const handleBlur = useCallback((field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));

    const fieldConfig = validationRules[field];
    if (fieldConfig) {
      const rules = Array.isArray(fieldConfig) ? fieldConfig : fieldConfig.rules || [];
      const generalError = fieldConfig.generalError;
      const value = formData[field];

      let firstFailedRule = null;
      for (const rule of rules) {
        if (!rule.check(value, formData)) {
          firstFailedRule = rule;
          break;
        }
      }

      setErrors((prevErrors) => {
        if (firstFailedRule) {
          return {
            ...prevErrors,
            [field]: generalError || firstFailedRule.message,
          };
        } else {
          if (prevErrors[field]) {
            const nextErrors = { ...prevErrors };
            delete nextErrors[field];
            return nextErrors;
          }
        }
        return prevErrors;
      });
    }
  }, [formData, validationRules]);

  /**
   * Validates all form fields, sets errors for any that fail, and returns true if valid.
   */
  const validateForm = useCallback(() => {
    const newErrors = {};
    const touchedFields = {};

    Object.keys(validationRules).forEach((field) => {
      touchedFields[field] = true;
      const fieldConfig = validationRules[field];
      const rules = Array.isArray(fieldConfig) ? fieldConfig : fieldConfig.rules || [];
      const generalError = fieldConfig.generalError;
      const value = formData[field];

      for (const rule of rules) {
        if (!rule.check(value, formData)) {
          newErrors[field] = generalError || rule.message;
          break;
        }
      }
    });

    setTouched(touchedFields);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, validationRules]);

  /**
   * Evaluates all rules for a field in real-time, returning their satisfies statuses.
   * Useful for showing checklists (e.g. password requirements) while typing.
   */
  const getFieldRuleStatuses = useCallback((field) => {
    const fieldConfig = validationRules[field];
    if (!fieldConfig) return [];

    const rules = Array.isArray(fieldConfig) ? fieldConfig : fieldConfig.rules || [];
    const value = formData[field] ?? '';

    return rules.map((rule) => ({
      text: rule.message,
      isSatisfied: !!rule.check(value, formData),
    }));
  }, [formData, validationRules]);

  /**
   * Resets the form back to its initial state.
   */
  const resetForm = useCallback(() => {
    setFormData(initialValues);
    setErrors({});
    setTouched({});
  }, [initialValues]);

  /**
   * Manually sets the form data (e.g., when loading existing data for editing).
   */
  const setValues = useCallback((newValues) => {
    setFormData(newValues);
  }, []);

  return {
    formData,
    errors,
    touched,
    handleChange,
    handleBlur,
    validateForm,
    getFieldRuleStatuses,
    resetForm,
    setFormData: setValues,
    setErrors,
  };
}
